import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { useIsCampaignEnabled } from 'modules/campaigns/hooks/useIsCampaignEnabled';
import useInsightV2FeatureFlag from 'modules/insights-v2/hooks/useInsightV2FeatureFlag';
import useInsightViewSwitch from 'modules/insights-v2/hooks/useInsightViewSwitch';
import { useMerchantContext } from 'modules/merchant/MerchantContext';
import { useIsSettingsPageEnabled } from 'modules/shared-menu/hooks/useIsSettingsPageEnabled';
import { useIsMerchantSharedMenuEnabled } from 'modules/shared-menu/hooks/useIsSharedMenuEnabled';
import { useGetOfferByMerchantID } from 'modules/wolt-capital/useGetOfferByMerchantID';

import { AvailableRouteItem } from './types';

type UseMerchantAvailableRoutesProps = {
  merchantId: string;
};

export const showInsights = (merchantVenues: Venue[]) => {
  return !merchantVenues.length || !!merchantVenues.find(venue => venue.type !== 'daas');
};

export const useMerchantAvailableRoutes = ({ merchantId }: UseMerchantAvailableRoutesProps) => {
  const intl = useIntl();

  const { merchant, merchantVenues = [] } = useMerchantContext();
  const isSharedMenuEnabled = useIsMerchantSharedMenuEnabled(merchantId);
  const isSharedMenuSettingsPageEnabled = useIsSettingsPageEnabled();

  const { merchantsOffer } = useGetOfferByMerchantID(merchantId);
  const isWoltCapitalEnabled = !!merchantsOffer;

  const { showInsightV1 } = useInsightViewSwitch();
  const { isInsightV2Enabled } = useInsightV2FeatureFlag('merchant', merchantId);

  const { isCampaignsEnabled } = useIsCampaignEnabled({
    feature: 'merchant',
    featureId: merchantId,
    entityCountry: merchant.country,
  });

  const items: AvailableRouteItem[] = useMemo(() => {
    const t = (id: string) => intl.formatMessage({ id });
    const insightsSubItems = [
      {
        label: t('insights.performance.header'),
        link: `/merchant/${merchantId}/insights/performance`,
        type: 'INSIGHTS_V2_PERFORMANCE' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_PERFORMANCE' as const,
      },
      {
        label: t('insights.operations.header'),
        link: `/merchant/${merchantId}/insights/operations`,
        type: 'INSIGHTS_V2_OPERATIONS' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_OPERATIONS' as const,
      },
      {
        label: t('insights.customer-base.header'),
        link: `/merchant/${merchantId}/insights/customer-base`,
        type: 'INSIGHTS_V2_CUSTOMER_BASE' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_CUSTOMER_BASE' as const,
      },
      {
        label: t('insights.menu.header'),
        link: `/merchant/${merchantId}/insights/menu`,
        type: 'INSIGHTS_V2_MENU' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_MENU' as const,
      },
      {
        label: t('insights.ratings-and-reviews.header'),
        link: `/merchant/${merchantId}/insights/ratings`,
        type: 'INSIGHTS_V2_RATINGS_AND_REVIEWS' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_RATINGS_AND_REVIEWS' as const,
      },
    ];
    return [
      ...(showInsights(merchantVenues)
        ? [
            {
              label: t('navigation.insights'),
              link: `/merchant/${merchantId}/insights`,
              type: !showInsightV1 && isInsightV2Enabled ? ('INSIGHTS_V2' as const) : ('INSIGHTS' as const),
              subItems: !showInsightV1 && isInsightV2Enabled ? insightsSubItems : [],
            },
          ]
        : []),
      {
        label: t('navigation.venues'),
        link: `/merchant/${merchantId}/venues`,
        type: 'VENUES' as const,
      },
      {
        label: t('navigation.reporting'),
        link: `/merchant/${merchantId}/reports`,
        type: 'REPORTS' as const,
      },
      {
        exact: true,
        label: t('navigation.purchase-data'),
        link: `/merchant/${merchantId}/purchase-data`,
        type: 'PURCHASES' as const,
      },
      {
        label: t('navigation.access-management'),
        link: `/merchant/${merchantId}/access`,
        type: 'ACCESS_MANAGEMENT' as const,
      },
      ...(isCampaignsEnabled
        ? [
            {
              label: t('campaigns.title'),
              link: `/merchant/${merchantId}/campaigns`,
              type: 'CAMPAIGNS' as const,
            },
          ]
        : []),
      ...(isSharedMenuEnabled
        ? [
            {
              label: t('listing-manager.title'),
              link: `/merchant/${merchantId}/shared-menu`,
              type: 'SHARED_MENU' as const,
              subItems: isSharedMenuSettingsPageEnabled
                ? [
                    {
                      label: t('listing-manager.settings.title'),
                      link: `/merchant/${merchantId}/shared-menu/settings`,
                      type: 'SHARED_MENU_SETTINGS' as const,
                    },
                  ]
                : [],
            },
          ]
        : []),
      ...(isWoltCapitalEnabled
        ? [
            {
              label: t('navigation.wolt-capital'),
              link: `/merchant/${merchantId}/wolt-capital`,
              type: 'WOLT_CAPITAL' as const,
            },
          ]
        : []),
    ];
  }, [
    merchantId,
    merchantVenues,
    showInsightV1,
    isCampaignsEnabled,
    isInsightV2Enabled,
    isSharedMenuEnabled,
    isSharedMenuSettingsPageEnabled,
    intl,
    isWoltCapitalEnabled,
  ]);

  return { items };
};
